import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generateOverviewPDF } from '../utils/pdfExport';

export default function OverviewScreen() {
  const location = useLocation();
  const navigate = useNavigate();

  // (#17) Fall back to localStorage if state is lost on refresh
  const [videos] = useState(() => {
    const fromState = location.state?.videos;
    if (fromState && fromState.length > 0) return fromState;
    try {
      return JSON.parse(localStorage.getItem('uploadedVideos') || '[]');
    } catch (_) { return []; }
  });

  const analyzed = videos.filter(v => v.result);
  const avgScore = analyzed.length > 0
    ? analyzed.reduce((s, v) => s + (parseFloat(v.result.average_rating) || 0), 0) / analyzed.length
    : 0;

  if (videos.length === 0) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa', padding: '24px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
          <button onClick={() => navigate('/')} style={iconBtn}>←</button>
          <h2 style={heading}>Overview</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid #e9ecef' }}>
          <p style={{ fontSize: '15px', color: '#6c757d', marginBottom: '20px' }}>No videos analyzed yet.</p>
          <button onClick={() => navigate('/')} style={primaryBtn}>Start Analysis</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/')} style={iconBtn}>←</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', margin: 0 }}>Overview</h2>
          <p style={{ fontSize: '12px', color: '#6c757d', margin: 0 }}>{analyzed.length} of {videos.length} analyzed</p>
        </div>
        {analyzed.length > 0 && (
          <button onClick={() => generateOverviewPDF(videos)}
            style={{ padding: '8px 14px', background: '#2c3e50', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', color: '#ffffff', flexShrink: 0 }}>
            Export PDF
          </button>
        )}
      </div>

      <div style={{ padding: '16px 20px' }}>
        {/* Stats row — #22 avg score larger + colored */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '20px', marginBottom: '20px', border: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{videos.length}</div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>Total Videos</div>
          </div>
          <div style={{ width: '1px', backgroundColor: '#e9ecef', alignSelf: 'stretch' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>{analyzed.length}</div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>Analyzed</div>
          </div>
          <div style={{ width: '1px', backgroundColor: '#e9ecef', alignSelf: 'stretch' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: '700', color: avgScore >= 8 ? '#10b981' : avgScore >= 6 ? '#d97706' : avgScore > 0 ? '#ef4444' : '#2c3e50' }}>{avgScore > 0 ? avgScore.toFixed(1) : '—'}</div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>Avg Score</div>
          </div>
        </div>

        {/* Video cards (#16 — show duration + best rep) */}
        <div style={{ display: 'grid', gap: '12px' }}>
          {videos.map((video, idx) => {
            const result = video.result || {};
            const rating = parseFloat(result.average_rating) || 0;
            const reps = result.total_reps ?? 0;
            const duration = result.duration_seconds;
            const bestRep = result.ratings?.length ? Math.max(...result.ratings) : null;
            const ratingColor = rating >= 8 ? '#10b981' : rating >= 6 ? '#d97706' : '#ef4444';
            const isAnalyzed = !!video.result;

            return (
              <div key={video.id || idx}
                onClick={() => isAnalyzed && navigate('/result', { state: { video } })}
                style={{ backgroundColor: '#ffffff', borderRadius: '14px', padding: '16px', border: '1px solid #e9ecef', cursor: isAnalyzed ? 'pointer' : 'default',
                  /* #21 colored left border based on score */
                  borderLeft: isAnalyzed ? `4px solid ${ratingColor}` : '1px solid #e9ecef' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', color: '#adb5bd', backgroundColor: '#f8f9fa', padding: '2px 8px', borderRadius: '6px' }}>#{idx + 1}</span>
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>{video.exerciseName}</span>
                    </div>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{video.videoName}</p>
                  </div>
                  {isAnalyzed ? (
                    <span style={{ fontSize: '16px', fontWeight: '700', color: ratingColor, flexShrink: 0, marginLeft: '12px' }}>{rating.toFixed(1)}/10</span>
                  ) : (
                    <span style={{ fontSize: '12px', color: '#adb5bd', flexShrink: 0, marginLeft: '12px' }}>Pending</span>
                  )}
                </div>

                {/* (#16) Metrics row */}
                {isAnalyzed && (
                  <div style={{ display: 'flex', gap: '16px', paddingTop: '10px', borderTop: '1px solid #f8f9fa' }}>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>{reps}</div>
                      <div style={{ fontSize: '10px', color: '#adb5bd', textTransform: 'uppercase' }}>Reps</div>
                    </div>
                    {duration != null && (
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>{duration.toFixed(1)}s</div>
                        <div style={{ fontSize: '10px', color: '#adb5bd', textTransform: 'uppercase' }}>Duration</div>
                      </div>
                    )}
                    {bestRep != null && (
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#10b981' }}>{bestRep.toFixed(1)}</div>
                        <div style={{ fontSize: '10px', color: '#adb5bd', textTransform: 'uppercase' }}>Best Rep</div>
                      </div>
                    )}
                    <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#adb5bd', alignSelf: 'center' }}>View →</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom actions — #23 differentiated: New Analysis clears, Upload More pre-selects exercise */}
      <div style={{ position: 'sticky', bottom: 0, padding: '16px', backgroundColor: '#ffffff', borderTop: '1px solid #e9ecef', boxShadow: '0 -4px 16px rgba(0,0,0,0.06)', display: 'flex', gap: '12px' }}>
        <button
          onClick={() => { localStorage.removeItem('uploadedVideos'); navigate('/'); }}
          style={{ flex: 1, padding: '14px', backgroundColor: '#2c3e50', color: '#ffffff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
          New Analysis
        </button>
        <button
          onClick={() => {
            const lastExercise = analyzed.length > 0 ? (analyzed[analyzed.length - 1].exerciseName || null) : null;
            navigate('/upload', { state: { exerciseName: lastExercise } });
          }}
          style={{ flex: 1, padding: '14px', backgroundColor: '#ffffff', color: '#2c3e50', border: '1px solid #e9ecef', borderRadius: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
          Upload More
        </button>
      </div>
    </div>
  );
}

const iconBtn = { width: '40px', height: '40px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '12px', fontSize: '20px', cursor: 'pointer', color: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const heading = { fontSize: '20px', fontWeight: '600', color: '#2c3e50', margin: 0 };
const primaryBtn = { padding: '12px 24px', backgroundColor: '#2c3e50', color: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' };
